import React from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Switch,
  Avatar,
  InputAdornment,
} from "@mui/material";
import { colors } from "../../../ui/colors";
import { AdminPanelSettings, DeleteOutline, Upload, Visibility, VisibilityOff } from "@mui/icons-material";

import { useAddCardV, RegisterSecretModalProps } from "../../hooks/useAddCardV";
import { useModals } from "../../../../context/ModalContext";
import formatMMSS from "../helpper/formatMMSS";




type Props = RegisterSecretModalProps & { modalKey: string };

const AddCardV: React.FC<Props> = ({ onSave, userCompanyId, remainingSeconds, modalKey }) => {
  const { modals, closeModal } = useModals();

  const closeThisModal = React.useCallback(() => {
    const candidates = (modals || []).filter((m: any) => m.key === modalKey && !m.minimized);
    if (!candidates.length) return;
    closeModal(candidates[candidates.length - 1].id);
  }, [modals, closeModal, modalKey]);

  const {
    title,
    setTitle,
    userEmail,
    setUserEmail,
    password,
    setPassword,
    website,
    setWebsite,
    notes,
    setNotes,
    highSecurity,
    setHighSecurity,
    showPassword,
    setShowPassword,
    avatarPreview,
    saving,
    error,
    fileInputRef,
    resetForm,
    handleImageChange,
    handleSave,
    canSave,
    removeImage,
    fieldErrors,
    touchField,
    runValidation,
    showFieldError,
  } = useAddCardV({
    onSave,
    userCompanyId,
    remainingSeconds,
    onAfterSave: closeThisModal, 
  });

  return (
    <Box sx={{ borderRadius: "16px", p: 2 }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography fontWeight="600" fontSize="20px">
            Create New Secret
          </Typography>
          <Typography fontSize="14px" color="text.secondary">
            Add a new credential to the enterprise vault
          </Typography>
        </Box>
        {typeof remainingSeconds === "number" ? (
            <Typography variant="caption" sx={{ color: colors.orange.base, fontWeight: 800, letterSpacing: 0.5 }}>
              Auto-lock in {formatMMSS(remainingSeconds)}
            </Typography>
          ) : null}
      </DialogTitle>

      <DialogContent sx={{ mt: 1 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" gap={2} justifyContent="space-between" sx={{ mt: 1 }}>
            <TextField
              value={title}
              size="small"
              onChange={(e) => {
                const v = e.target.value;
                touchField("title");
                setTitle(v);
                runValidation({ title: v });
              }}
              label="Secret Name"
              sx={{ flex: 1 }}
              disabled={saving}
              error={showFieldError("title")}
              helperText={showFieldError("title") ? fieldErrors.title : ""}
            />

            <TextField
              value={website}
              size="small"
              onChange={(e) => {
                const v = e.target.value;
                touchField("websiteUrl");
                setWebsite(v);
                runValidation({ website: v });
              }}
              label="Website URL"
              placeholder="https://..."
              sx={{ flex: 1 }}
              disabled={saving}
              error={showFieldError("websiteUrl")}
              helperText={showFieldError("websiteUrl") ? fieldErrors.websiteUrl : ""}
            />
          </Box>

          <Box display="flex" gap={2}>
            <TextField
              label="Email"
              value={userEmail}
              size="small"
              onChange={(e) => {
                const v = e.target.value;
                touchField("usernameEmail");
                setUserEmail(v);
                runValidation({ userEmail: v });
              }}
              placeholder="admin@company.com"
              sx={{ flex: 1 }}
              disabled={saving}
              error={showFieldError("usernameEmail")}
              helperText={showFieldError("usernameEmail") ? fieldErrors.usernameEmail : ""}
            />

            <TextField
              type={showPassword ? "text" : "password"}
              value={password}
              size="small"
              onChange={(e) => {
                const v = e.target.value;
                touchField("password");
                setPassword(v);
                runValidation({ password: v });
              }}
              label="Password"
              placeholder="••••••••"
              sx={{ flex: 1 }}
              disabled={saving}
              error={showFieldError("password")}
              helperText={showFieldError("password") ? fieldErrors.password : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end" disabled={saving}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1 }}>
            <Avatar src={avatarPreview ?? undefined} sx={{ width: 36, height: 36 }} />

            <Button component="label" variant="outlined" startIcon={<Upload />} sx={{ borderRadius: "8px", textTransform: "none" }} disabled={saving}>
              Upload image
              <input ref={fileInputRef} hidden accept="image/*" type="file" onChange={handleImageChange} />
            </Button>

            {avatarPreview ? (
              <IconButton onClick={removeImage} size="small" sx={{ ml: "auto" }} disabled={saving}>
                <DeleteOutline />
              </IconButton>
            ) : null}
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes"
            value={notes}
            onChange={(e) => {
              const v = e.target.value;
              touchField("notes");
              setNotes(v);
              runValidation({ notes: v });
            }}
            placeholder="Add additional details or context..."
            sx={{ mt: 2 }}
            disabled={saving}
          />

          {error ? (
            <Typography color="error" sx={{ mt: 0.5 }}>
              {error}
            </Typography>
          ) : null}

          <Divider sx={{ my: 1 }} />

          <Box>
            <Box display="flex" mb={1}>
              <AdminPanelSettings sx={{ mr: 1 }} />
              <Typography fontWeight={600}>SECURITY & ACCESS</Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" gap={1} alignItems="center">
              <Box display="flex" alignItems="center" gap={2} sx={{ backgroundColor: "var(--fx-surface-sunken, #F8FAFC)", p: 2, borderRadius: "12px" }}>
                <Box>
                  <Typography fontWeight="600">High Security Mode</Typography>
                  <Typography fontSize="12px" color="text.secondary">
                    Requires MFA for every access
                  </Typography>
                </Box>

                <Switch
                  checked={highSecurity}
                  onChange={(e) => setHighSecurity(e.target.checked)}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: colors.orange.base },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: colors.orange.base,
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", gap: 2, px: 3, pb: 3 }}>
        <Button variant="text" onClick={resetForm} sx={{ textTransform: "none", color: "text.secondary" }} disabled={saving}>
          Cancel
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!canSave}
            sx={{
              textTransform: "none",
              backgroundColor: colors.orange.base,
              borderRadius: "8px",
              px: 3,
              "&:hover": { backgroundColor: "#e56f00" },
            }}
          >
            {saving ? "Saving..." : "Save Secret"}
          </Button>

          
        </Box>
      </DialogActions>
    </Box>
  );
};

export default AddCardV;
